import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { cloneDeep } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IsLoading } from '../../decorators';
import { UniversalSearchModel } from '../../../core/models';
import { JobDepartmentModule, JobLevelModule, JobTitleModule } from '../../components';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { CreateEditBuyerPersonaGroupService } from './services/create-edit-buyer-persona-group.service';

const LOADING_TAG: string = 'create-edit-buyer-persona-group-loading-tag';

export interface BuyerPersonaGroupData {
  id?: number;
  name?: string;
  query?: any;
}

@UntilDestroy()
@Component({
  selector: 'app-create-edit-buyer-persona-group',
  templateUrl: './create-edit-buyer-persona-group.component.html',
  imports: [
    MatFormField,
    JobLevelModule,
    JobDepartmentModule,
    JobTitleModule,
    MatInput,
    ReactiveFormsModule,
    MatLabel,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CreateEditBuyerPersonaGroupService],
})
export class CreateEditBuyerPersonaGroupComponent implements OnInit, OnChanges, OnDestroy {
  @IsLoading(LOADING_TAG) readonly isLoading$: Observable<boolean>;

  @Input() data: BuyerPersonaGroupData;
  @Output() isValidChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<void>();

  controlData: any = {};
  query: any = {};
  id: number;

  readonly nameControl: FormControl = new FormControl('', [Validators.required, Validators.minLength(5), Validators.maxLength(100)]);

  constructor(
    private readonly service: CreateEditBuyerPersonaGroupService,
  ) {
  }

  ngOnInit(): void {
    this.initializeData();

    this.nameControl.valueChanges.pipe(
      untilDestroyed(this),
    ).subscribe(() => this.updateValidity());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.initializeData();
    }
  }

  private initializeData(): void {
    const { job_levels, job_departments } = cloneDeep(UniversalSearchModel);

    this.id = this.data?.id;

    if (this.data) {
      this.controlData = {
        ...(this.data?.query || {}),
        ...this.service.invertTransformTruthMapCsv(
          (this.data?.query || {}),
          ['job_levels', 'job_departments'],
          {
            job_levels: { ...job_levels },
            job_departments: { ...job_departments },
          }),
      };
      this.nameControl.setValue(this.data?.name || '');
    } else {
      this.controlData = {
        job_levels: { ...job_levels },
        job_departments: { ...job_departments },
      };
    }
    this.onSelectionChanged();
  }

  onSelectionChanged(): void {
    this.query = this.service.criteriaToRequest(this.controlData);
    this.updateValidity();
  }

  getQuery(): any {
    return this.query;
  }

  getName(): string {
    return this.nameControl.value;
  }

  ngOnDestroy(): void {
    this.nameControl.reset();
    this.query = {};
    this.updateValidity();
  }

  isValid(): boolean {
    return this.nameControl.valid &&
           (this.query?.['job_departments']?.length ||
            this.query?.['job_levels']?.length ||
            this.query?.['job_sub_departments']?.length ||
            this.query?.['job_title']?.length ||
            this.query?.['job_title_any']?.length ||
            this.query?.['job_title_all']?.length ||
            this.query?.['job_title_none']?.length);
  }

  save(): Observable<any> {
    if (!this.isValid()) {
      return of(null);
    }

    const name = this.getName();
    const id = this.id;
    const query = this.getQuery();

    return this.service.saveBuyerPersona(id, name, query)
      .pipe(
        tap(() => this.saved.emit())
      );
  }

  private updateValidity(): void {
    this.isValidChange.emit(this.isValid());
  }
}
